function calendarTime(year: number, month: number, day: number) {
  const time = Date.UTC(year, month, day);
  const date = new Date(time);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month && date.getUTCDate() === day
    ? time
    : null;
}

function withClock(date: number | null, parts: RegExpExecArray) {
  let hour = Number(parts[4] ?? 0);
  const minute = Number(parts[5] ?? 0);
  const second = Number(parts[6] ?? 0);
  const meridiem = parts[7]?.toUpperCase();
  if (date === null || minute > 59 || second > 59 || (meridiem ? hour < 1 || hour > 12 : hour > 23))
    return null;
  if (meridiem) hour = (hour % 12) + (meridiem === 'PM' ? 12 : 0);
  const offset = /^([+-])(\d{2}):?(\d{2})$/.exec(parts[8] ?? '');
  if (offset && (Number(offset[2]) > 23 || Number(offset[3]) > 59)) return null;
  const offsetMinutes = offset
    ? (Number(offset[2]) * 60 + Number(offset[3])) * (offset[1] === '-' ? -1 : 1)
    : 0;
  return date + ((hour * 60 + minute - offsetMinutes) * 60 + second) * 1000;
}

export function documentTime(value: string | null | undefined) {
  if (!value) return null;
  const text = value.trim();
  const yearNumber = (value: string) => {
    const year = Number(value);
    return value.length === 2 ? year + (year < 50 ? 2000 : 1900) : year;
  };
  const us =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})(?: (\d{1,2}):(\d{2})(?::(\d{2}))?(?: (AM|PM))?(?: (Z|UTC|GMT|[+-]\d{2}:?\d{2}))?)?$/i.exec(
      text,
    );
  if (us) return withClock(calendarTime(yearNumber(us[3]!), Number(us[1]) - 1, Number(us[2])), us);
  const dmy =
    /^(\d{1,2})[- ]([A-Z]+)[- ](\d{4}|\d{2})(?: (\d{1,2}):(\d{2})(?::(\d{2}))?(?: (AM|PM))?(?: (Z|UTC|GMT|[+-]\d{2}:?\d{2}))?)?$/i.exec(
      text,
    );
  if (dmy) {
    const month = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december',
    ].findIndex(
      (name) => name === dmy[2]!.toLowerCase() || name.slice(0, 3) === dmy[2]!.toLowerCase(),
    );
    if (month < 0) return null;
    return withClock(calendarTime(yearNumber(dmy[3]!), month, Number(dmy[1])), dmy);
  }
  // Socrata's floating timestamps describe city wall-clock dates. Anchor them
  // consistently before the timeline's UTC formatting; a browser-local parse
  // can change the day and disagree with server-rendered text.
  // Do not fall back to host-local Date.parse for unknown city date formats.
  // Different ordering changes AI record signatures as well as rendered dates.
  const iso =
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/.exec(
      text,
    );
  if (!iso || calendarTime(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) === null)
    return null;
  if (Number(iso[4] ?? 0) > 23 || Number(iso[5] ?? 0) > 59 || Number(iso[6] ?? 0) > 59) return null;
  const time = Date.parse(iso[4] && !iso[7] ? `${text}Z` : text);
  return Number.isFinite(time) ? time : null;
}
